import {
  createStore,
  createStateHook,
  createActionsHook,
  Action,
  createContainer,
  createSubscriber,
} from 'react-sweet-state';

import { removeUrlHashPrefix } from 'src/components/file-tree/utils';
import { Diff } from 'src/types/pull-request';
import { extractFilepath } from 'src/utils/extract-file-path';
import store from 'src/utils/store';

import { ReviewedFilesState, ReviewedFilesContainerProps } from './types';

/*
The logic for Marking Files as Viewed is as follows:

- When a file is marked as viewed, we save the current file hash in local storage
  with such <file name: file hash> key value pairs. This represents that the user reviewed the file and
  the state of the file when the they viewed it. We also add it to our react-sweet-state store,
  which consists of a set including all "reviewed" file names. See "toggleDiffReviewed".

- When the diff loads or is updated, we compare the new file hashes with the values in
  local storage, to identify if files have been updated since the user reviewed them.
  We then store all still "reviewed" file names in our react-sweet-state set, which then
  serves as the official source of truth.

- All components, such as the viewed checkboxes, file headers, and file tree subscribe
  to the react-sweet-state store via hooks to see if their respective file has been viewed.

- Since this is currently a frontend-only feature, we maintain local storage to persist
  reviewed states between page loads.
*/

// Initial State
const initialState: ReviewedFilesState = {
  diffsReviewedSet: new Set(),
  diffsReviewedKey: '',
};

// Actions
const toggleDiffReviewed =
  (
    filepath: string,
    isChecked: boolean,
    fileIndex: string[]
  ): Action<ReviewedFilesState> =>
  ({ setState, getState }) => {
    const { diffsReviewedSet, diffsReviewedKey } = getState();
    const newDiffsReviewedSet = new Set(diffsReviewedSet);
    const localStorageState = { ...store.get(diffsReviewedKey) };
    if (isChecked) {
      /*
    The file's diff index is displayed as a string in the form of '12345..78910',
    which contains the hashes of a file at the merge-base '12345'
    and head of the source branch '78910'. We filter the index for the latter,
    to get the current file hash and store it in the reviewed local storage.
    */
      const currentFileHash =
        fileIndex.length && fileIndex[0].includes('..')
          ? fileIndex[0].split('..')[1]
          : 'empty';

      newDiffsReviewedSet.add(filepath);
      localStorageState[filepath] = currentFileHash;
    } else {
      newDiffsReviewedSet.delete(filepath);
      delete localStorageState[filepath];
    }

    setState({ diffsReviewedSet: newDiffsReviewedSet });
    store.setStrictlyNecessaryItem(diffsReviewedKey, localStorageState);
  };

export const unmarkAllViewed =
  (): Action<ReviewedFilesState> =>
  ({ setState, getState }) => {
    const { diffsReviewedKey } = getState();
    store.setStrictlyNecessaryItem(diffsReviewedKey, {});
    setState({ diffsReviewedSet: new Set() });
  };

export const markAllViewed =
  (allDiffFiles: Diff[]): Action<ReviewedFilesState> =>
  ({ setState, getState }) => {
    const { diffsReviewedKey } = getState();
    const filepaths = new Set<string>();
    const localStorageState: { [key: string]: string } = {};

    for (const file of allDiffFiles) {
      if (!file.index.length || !file.index[0].includes('..')) {
        continue;
      }
      const filepath = extractFilepath(file);
      filepaths.add(filepath);
      const hash = file.index[0].split('..')[1];
      localStorageState[filepath] = hash;
    }

    store.setStrictlyNecessaryItem(diffsReviewedKey, localStorageState);
    setState({ diffsReviewedSet: filepaths });
  };

const actions = {
  toggleDiffReviewed,
  unmarkAllViewed,
  markAllViewed,
};

const ReviewedFilesStore = createStore({
  initialState,
  actions,
});

export const ReviewedFilesContainer = createContainer(ReviewedFilesStore, {
  onInit:
    () =>
    ({ setState }, { diffsReviewedKey }: ReviewedFilesContainerProps) => {
      setState({ diffsReviewedKey });
    },
  onUpdate:
    () =>
    (
      { setState },
      {
        diffsReviewedKey,
        allDiffFiles,
        handleUpdatedFiles,
      }: ReviewedFilesContainerProps
    ) => {
      if (!allDiffFiles || !diffsReviewedKey) return;

      const initialReviewedState = store.get(diffsReviewedKey) || {};
      const initialReloadedSet: Set<string> = new Set();
      const updatedFiles: string[] = [];

      for (const file of allDiffFiles) {
        // Diff files are constructed using the buildDiffFiles function. The
        // inputs to that are the diff and the diff stat. Those requests are
        // async, and usually the diff stat returns first. That means we may be
        // here without all the information we need to determine file hashes.
        // We should not do anything destructive, e.g. change localstorage
        // values, until we're sure we have the latest data.

        let newFileHash;
        // If the file index is missing, there are 2 possible scenarios:
        // 1) The file diff only includes whitespace changes and the user set whitespace changes to be ignored in
        // their diff settings. In this case, the diffstat will show 0 additions and 0 deletions and we set the new
        // file hash to 'empty' like we do when they initially mark the file as viewed. We would not be able to discern
        // between whitespace changes (e.g. going from 2 spaces to 4 spaces) but that should be okay since the user has
        // chosen to ignore whitespace changes and would see the same 'file contents unchanged' diff.
        // TODO: update isFileContentsUnchanged in src/redux/pull-request/selectors/index.ts line 1196 to just check
        // lines_added and lines_removed, and use that here instead
        // 2) The file diff is not available or fully loaded in redux yet. In this case, we skip the iteration and let
        // the onUpdate function run again when the diff is available.
        if (!file.index.length || !file.index[0].includes('..')) {
          if (file.additions === 0 && file.deletions === 0) {
            newFileHash = 'empty';
          } else {
            continue;
          }
        } else {
          // This is the normal case
          newFileHash = file.index[0].split('..')[1];
        }

        const filepath = extractFilepath(file);
        const oldFileHash = initialReviewedState[filepath];

        // skip to next iteration if current file was not marked as reviewed
        if (!oldFileHash) {
          continue;
        }

        if (newFileHash === oldFileHash) {
          // If the hashes match then we add the file to the reviewed set
          initialReloadedSet.add(filepath);
        } else {
          // Othwerwise, we get ready to remove the file from local storage and the reviewed set
          delete initialReviewedState[filepath];
          updatedFiles.push(filepath);
        }
      }

      handleUpdatedFiles(updatedFiles);
      setState({
        diffsReviewedSet: initialReloadedSet,
        diffsReviewedKey,
      });
      store.setStrictlyNecessaryItem(diffsReviewedKey, initialReviewedState);
    },
});

export const DiffReviewedSubscriber = createSubscriber(ReviewedFilesStore);

export const useReviewedFiles = createActionsHook(ReviewedFilesStore);

// Selectors
const getSingleDiffReviewed = (
  state: ReviewedFilesState,
  filepath: string | null
): boolean => (filepath ? state.diffsReviewedSet.has(filepath) : false);

export const useSingleDiffReviewed = createStateHook(ReviewedFilesStore, {
  selector: getSingleDiffReviewed,
});

const getNumFilesReviewed = (state: ReviewedFilesState): number => {
  return state.diffsReviewedSet.size;
};

export const useNumFilesReviewed = createStateHook(ReviewedFilesStore, {
  selector: getNumFilesReviewed,
});

export const getNextUnreviewedFileId = (
  state: ReviewedFilesState,
  payload: {
    filePath: string;
    orderedHrefs: string[];
    isSingleFileModeActive: boolean;
  }
): string | null => {
  const { filePath, orderedHrefs, isSingleFileModeActive } = payload;
  // return early to save unnecessary computation when single file mode is not active
  if (!isSingleFileModeActive) {
    return null;
  }

  // use removeUrlHashPrefix to exclude '#chg-' which is included in beginning of file hrefs (rest is the filepath)
  const fileIndex = orderedHrefs.findIndex(
    href => removeUrlHashPrefix(href) === filePath
  );

  const remainingFileHrefs = orderedHrefs.slice(fileIndex + 1);
  const unreviewedFileHref = remainingFileHrefs.find(
    href => !getSingleDiffReviewed(state, removeUrlHashPrefix(href))
  );

  return unreviewedFileHref ? unreviewedFileHref.slice(1) : null;
};

export const useNextUnreviewedFileId = createStateHook(ReviewedFilesStore, {
  selector: getNextUnreviewedFileId,
});

/** Variation of getNextUnreviewedFileId that allows passing a filePath at call
 time rather than at hook initialization time. This allows for the hook's
 value to remain stable even if the active file is changing, preventing
 re-renders. */
export const getNextUnreviewedFileIdCallback: (
  state: ReviewedFilesState,
  payload: { orderedHrefs: string[]; isSingleFileModeActive: boolean }
) => (filePath: string) => string | null = (state, payload) => {
  return (filePath: string) =>
    getNextUnreviewedFileId(state, {
      filePath,
      orderedHrefs: payload.orderedHrefs,
      isSingleFileModeActive: payload.isSingleFileModeActive,
    });
};

/** Variation of useNextUnreviewedFileId that allows passing a filePath at call
 time rather than at hook initialization time. This allows for the hook's
 value to remain stable even if the active file is changing, preventing
 re-renders. */
export const useNextUnreviewedFileIdCallback = createStateHook(
  ReviewedFilesStore,
  {
    selector: getNextUnreviewedFileIdCallback,
  }
);
